import { orderTabKeys } from "./constants";

export {
  hasNullRequiredField,
  normalizeTouchedKey,
  setDeepImmutable,
  toPath,
} from "../../shared/helpers/objectPath";

export const buildLicensePlateOptions = (vehicleList?: string[]) =>
  Array.isArray(vehicleList)
    ? vehicleList.map((item) => ({ label: item, value: item }))
    : [];

export {
  buildOrderCostReviewPayload,
  getAllowedOrderCostReviewStatuses,
  isOrderCostReviewPayloadAllowed,
  isOrderCostReviewableLine,
  mapAllEditableNodesToPayload,
  normalizeDetailFieldValue,
  setByPath,
  shouldAdvanceOrderCostReview,
  updateTreeById,
} from "../../shared/helpers/orderCostTree";

export const formatAmount = (value: any) =>
  Number.isFinite(Number(value)) ? `${Number(value).toLocaleString()}đ` : "0đ";

export const getSafeOrderTab = (tab?: string | null) =>
  orderTabKeys.includes(String(tab)) ? String(tab) : "1";

export const orderFeatureByTab = {
  "1": "ORDER_CREATION",
  "2": "ORDER_DISPATCHING",
  "3": "ORDER_HISTORY",
} as const;

type OrderTab = keyof typeof orderFeatureByTab;
export type FeaturePermissionMap = Readonly<
  Record<string, readonly string[] | undefined>
>;

export const getOrderFeatureByTab = (tab?: string | null) =>
  orderFeatureByTab[getSafeOrderTab(tab) as OrderTab];

export const getFirstAllowedOrderTab = (
  features?: FeaturePermissionMap,
) =>
  orderTabKeys.find((tab) =>
    features?.[orderFeatureByTab[tab as OrderTab]]?.includes("view"),
  );

export const getAllowedOrderTab = (
  tab: string | null | undefined,
  features?: FeaturePermissionMap,
) => {
  const safeTab = getSafeOrderTab(tab);
  const feature = getOrderFeatureByTab(safeTab);

  return features?.[feature]?.includes("view")
    ? safeTab
    : getFirstAllowedOrderTab(features);
};

export const shouldFetchOrderList = (
  activeTab: string,
  requestedTab: string | null | undefined,
  features?: FeaturePermissionMap,
) => {
  const requestedTabValue = String(requestedTab);
  if (!orderTabKeys.includes(requestedTabValue)) return false;

  return (
    requestedTabValue === activeTab &&
    getAllowedOrderTab(requestedTabValue, features) === requestedTabValue
  );
};

export const canExportOrderTab = (
  tab: string | null | undefined,
  features?: FeaturePermissionMap,
) => {
  const safeTab = getSafeOrderTab(tab);
  if (safeTab === "1") return false;

  return Boolean(
    features?.[getOrderFeatureByTab(safeTab)]?.includes("export"),
  );
};

export const canSaveOrderRow = (
  isNew: boolean,
  activeTab: string,
  features?: FeaturePermissionMap,
) => {
  const feature = isNew
    ? "ORDER_CREATION"
    : getOrderFeatureByTab(activeTab);
  const action = isNew ? "add" : "edit";

  return Boolean(features?.[feature]?.includes(action));
};

export type OrderDetailRequestIdentity = Readonly<{
  id: number;
  tab: string;
}>;

export const isCurrentOrderDetailRequest = (
  request: OrderDetailRequestIdentity,
  currentRequest: OrderDetailRequestIdentity,
) => request.id === currentRequest.id && request.tab === currentRequest.tab;

export const getOrderDirection = (record: any) =>
  record?.direction ||
  record?.businessPlan?.routeBase?.direction ||
  record?.routeBase?.direction ||
  "";

export const getOrderRoutePreviewMeta = (option: any) => {
  if (!option?.title || typeof option.title !== "string") return {};

  try {
    const meta = JSON.parse(option.title);

    return {
      receiptAddress: meta?.receiptAddress,
      deliveryAddress: meta?.deliveryAddress,
      direction: meta?.direction,
    };
  } catch {
    return {};
  }
};

const getLookupValue = (value: any) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "object") {
    return value.value ?? value._id ?? value.raw?._id ?? value.key;
  }

  return value;
};

const hasLookupValue = (value: any) => getLookupValue(value) !== undefined;

type OrderEditItem = {
  record?: any;
  touchedFields?: Record<string, boolean>;
};

type OrderRouteLockInput = {
  record?: any;
  editItem?: OrderEditItem;
};

const hasOrderBusinessPlanValue = ({ record, editItem }: OrderRouteLockInput) => {
  const editRecord = editItem?.record;

  if (editItem?.touchedFields?.businessPlanCode) {
    return hasLookupValue(editRecord?.businessPlanCode);
  }

  return (
    hasLookupValue(editRecord?.businessPlanCode) ||
    hasLookupValue(editRecord?.businessPlan) ||
    hasLookupValue(record?.businessPlanCode) ||
    hasLookupValue(record?.businessPlan)
  );
};

const hasOrderRouteBaseValue = ({ record, editItem }: OrderRouteLockInput) => {
  const editRecord = editItem?.record;

  if (editItem?.touchedFields?.routeCode) {
    return hasLookupValue(editRecord?.routeCode);
  }

  return (
    hasLookupValue(editRecord?.routeCode) ||
    hasLookupValue(editRecord?.routeBase) ||
    hasLookupValue(record?.routeCode) ||
    hasLookupValue(record?.routeBase)
  );
};

export const shouldDisableOrderBusinessPlanSelect = ({
  record,
  editItem,
}: OrderRouteLockInput) => {
  const editRecord = editItem?.record;
  const customerId =
    editRecord?.customerName?.value ??
    editRecord?.customer?._id ??
    record?.customer?._id;

  if (!customerId) return true;
  if (hasOrderBusinessPlanValue({ record, editItem })) return false;

  return hasOrderRouteBaseValue({ record, editItem });
};

export const shouldDisableOrderRouteBaseSelect = (input: OrderRouteLockInput) =>
  hasOrderBusinessPlanValue(input);

export const getOrderLookupPayloadValue = (
  selectedValue: any,
  fallbackValue: any,
  touched?: boolean,
) => {
  const selectedId = getLookupValue(selectedValue);
  const fallbackId = getLookupValue(fallbackValue);
  const isScalarSelected =
    typeof selectedValue === "string" || typeof selectedValue === "number";

  if (isScalarSelected && fallbackId !== undefined) {
    return fallbackId;
  }

  if (touched) {
    return selectedId ?? null;
  }

  return selectedId ?? fallbackId;
};

export const buildOrderRoutePayload = (
  rowEdit: any,
  touchedFields: Record<string, boolean> = {},
) => {
  const businessPlan = getOrderLookupPayloadValue(
    rowEdit?.businessPlanCode,
    rowEdit?.businessPlan,
    touchedFields.businessPlanCode,
  );
  const routeBase = getOrderLookupPayloadValue(
    rowEdit?.routeCode,
    rowEdit?.routeBase,
    touchedFields.routeCode,
  );
  const payload: Record<string, any> = {};

  if (businessPlan) {
    payload.businessPlan = businessPlan;
    return payload;
  }

  if (businessPlan === null) {
    payload.businessPlan = null;
  }

  if (routeBase !== undefined && routeBase !== null) {
    payload.routeBase = routeBase;
  }

  return payload;
};

export const isDraftOrderRecord = (record: any) =>
  !!record &&
  (record.state === "new" ||
    record.status === "new" ||
    String(record._id || "").startsWith("create-"));

export const acquireOrderSaveLock = (
  locks: Set<string>,
  orderId: string | number,
) => {
  const key = String(orderId);
  if (locks.has(key)) return false;

  locks.add(key);
  return true;
};

export const releaseOrderSaveLock = (
  locks: Set<string>,
  orderId: string | number,
) => {
  locks.delete(String(orderId));
};

export const normalizeCombinedRateForPayload = (
  value: any,
  touched?: boolean,
) => {
  if ((value === undefined || value === null || value === "") && touched) {
    return null;
  }

  return value;
};

export const shouldResetOrderTruckVendorOnFieldChange = (
  key: string | string[],
) => {
  const field = Array.isArray(key) ? key.join(".") : key;

  return ["businessPlanCode", "routeCode", "ownership"].includes(field);
};

export type OrderPaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

export const resetOrderPaginationToFirstPage = (
  pagination: OrderPaginationState,
): OrderPaginationState => ({
  ...pagination,
  current: 1,
});

export const getOrderPaginationAfterDelete = (
  pagination: OrderPaginationState,
): OrderPaginationState => {
  const nextTotal = Math.max(Number(pagination.total || 0) - 1, 0);
  const pageSize = Math.max(Number(pagination.pageSize) || 1, 1);
  const maxPage = Math.max(Math.ceil(nextTotal / pageSize), 1);

  return {
    ...pagination,
    total: nextTotal,
    current: Math.min(Number(pagination.current) || 1, maxPage),
  };
};
